<script setup lang="ts">
// @ts-expect-error — layout composable is JS without type declarations
import { useLayout } from '@/layout/composables/layout'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspaceStore'
import MenuIcon from '@/components/common/MenuIcon.vue'
import { ROUTE_HUB_ADMIN, ROUTE_HUB_CONFIGURAZIONE, useHubVoci } from '@/composables/useHubVoci'

export interface MenuItemDefinition {
  label: string
  icon?: string
  to?: string
  legacyUrl?: string
  isLegacy?: boolean
  permission?: string
  children?: MenuItemDefinition[]
  /**
   * Se true, la voce è visibile solo quando azienda.abilitaLotti === true.
   * Usato nel fallback statico; il menu dinamico da API filtra server-side.
   */
  requiresAziendaLotti?: boolean
  /**
   * Se true, la voce è visibile solo ai super-admin.
   * Usato nella fase ibrida Seam/Spring per nascondere le voci di gestione permessi
   * Spring agli admin aziendali (che gestiscono gli utenti tramite Seam).
   */
  superAdminOnly?: boolean
}

const { layoutState, isDesktop, isSlim, isHorizontal, hasOverlaySubmenu } = useLayout()
const route = useRoute()
const workspaceStore = useWorkspaceStore()

const { t } = useI18n()
const props = defineProps<{
  item: Record<string, unknown>
  index?: number
  root?: boolean
  parentPath?: string | null
}>()

// «Configurazione» è una foglia verso l'hub: resta evidenziata anche sulle pagine di configurazione
// (che non sono più nell'albero del menu).
const { hubDiRoute } = useHubVoci()
const attivaPerHub = computed(() => {
  const to = props.item['to']
  if (to !== ROUTE_HUB_CONFIGURAZIONE && to !== ROUTE_HUB_ADMIN) return false
  return hubDiRoute(route.path) === (to === ROUTE_HUB_ADMIN ? 'admin' : 'configurazione')
})

const fullPath = computed(() =>
  props.item['path']
    ? props.parentPath
      ? (props.parentPath as string) + (props.item['path'] as string)
      : (props.item['path'] as string)
    : null
)

// Root senza icona = section header in modalità verticale (label testuale, figli sempre visibili).
// In horizontal mode i nodi senza icona devono restare <a> cliccabili, altrimenti il CSS
// li rende invisibili (section header è un <div> che viene nascosto dal layout horizontal).
const isSection = computed(() =>
  props.root && !props.item['icon'] && !(isHorizontal as { value: boolean }).value
)

const isActive = computed(() =>
  props.item['path']
    ? (layoutState.activePath as string | null)?.startsWith(fullPath.value ?? '')
    : layoutState.activePath === props.item['to']
)

function itemClick(event: Event, item: Record<string, unknown>) {
  if (item['disabled']) {
    event.preventDefault()
    return
  }

  if (typeof item['command'] === 'function') {
    item['command']({ originalEvent: event, item })
  }

  if (item['items']) {
    // Voce con figli: espande/collassa il sottomenu — comportamento invariato.
    if (isActive.value) {
      layoutState.activePath = (layoutState.activePath as string).replace(
        item['path'] as string,
        ''
      )
    } else {
      layoutState.activePath = fullPath.value
      layoutState.menuHoverActive = true
    }
  } else {
    // Foglia normale (fuori da workspace, o link legacy): comportamento standard.
    layoutState.overlayMenuActive = false
    layoutState.mobileMenuActive = false
    layoutState.menuHoverActive = false
    layoutState.menuProfileActive = false

    if ((hasOverlaySubmenu as { value: boolean }).value && isDesktop()) {
      layoutState.activePath = null
    }
  }
}

function onMouseEnter() {
  if (
    isDesktop() &&
    props.root &&
    props.item['items'] &&
    (layoutState.menuHoverActive as boolean) &&
    !isActive.value
  ) {
    layoutState.activePath = fullPath.value
  }
}

const tooltipValue = computed(() =>
  (isSlim as { value: boolean }).value && props.root && !isActive.value
    ? (props.item['label'] as string)
    : null
)

/**
 * True quando siamo in workspace E la voce è una foglia Vue (ha `to`, non ha `url`).
 * In questo caso renderizziamo un `<a>` intercettato invece del router-link standard,
 * per evitare che Vue Router navighi prima che il nostro handler possa bloccare.
 */
const isWorkspaceVueLeaf = computed(() =>
  route.name === 'workspace' &&
  !!props.item['to'] &&
  !props.item['url'] &&
  !props.item['items']
)

function workspaceLeafClick(event: Event, item: Record<string, unknown>) {
  event.preventDefault()
  workspaceStore.requestOpen(item['to'] as string)
  layoutState.overlayMenuActive = false
  layoutState.mobileMenuActive = false
  layoutState.menuHoverActive = false
  layoutState.menuProfileActive = false
  if ((hasOverlaySubmenu as { value: boolean }).value && isDesktop()) {
    layoutState.activePath = null
  }
}
</script>

<template>
  <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActive }">
    <!-- Section header: root senza icona → solo testo, nessun link cliccabile -->
    <div
      v-if="isSection && item['visible'] !== false"
      class="layout-menuitem-root-text"
    >
      <span>{{ item['label'] }}</span>
      <i class="layout-menuitem-root-icon pi pi-fw pi-ellipsis-h" />
    </div>

    <!-- Gruppo collassabile o link esterno (non section) -->
    <a
      v-if="!isSection && (!item['to'] || item['items']) && item['visible'] !== false"
      v-tooltip.hover="tooltipValue"
      :href="(item['url'] as string) || undefined"
      :target="(item['target'] as string) || undefined"
      :class="item['class'] as string"
      tabindex="0"
      @click="itemClick($event, item)"
      @mouseenter="onMouseEnter"
    >
      <MenuIcon :icon="item['icon'] as string" />
      <span class="layout-menuitem-text">{{ item['label'] }}</span>
      <Tag
        v-if="item['nuova']"
        :value="t('layout.menu.nuovo', 'Nuovo')"
        severity="success"
        class="menu-badge-nuovo"
      />
      <i
        v-if="item['items']"
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
      />
    </a>

    <!-- Foglia in workspace mode: <a> intercettato che carica nel pannello attivo -->
    <a
      v-if="isWorkspaceVueLeaf && item['visible'] !== false"
      v-tooltip.hover="tooltipValue"
      href="#"
      :class="item['class'] as string"
      tabindex="0"
      @click.prevent="workspaceLeafClick($event, item)"
      @mouseenter="onMouseEnter"
    >
      <MenuIcon :icon="item['icon'] as string" />
      <span class="layout-menuitem-text">{{ item['label'] }}</span>
      <Tag
        v-if="item['nuova']"
        :value="t('layout.menu.nuovo', 'Nuovo')"
        severity="success"
        class="menu-badge-nuovo"
      />
    </a>

    <!-- Foglia: Vue Router link (fuori da workspace) -->
    <router-link
      v-if="item['to'] && !item['items'] && item['visible'] !== false && !isWorkspaceVueLeaf"
      v-tooltip.hover="tooltipValue"
      :to="(item['to'] as string)"
      exact-active-class="active-route"
      :class="[item['class'] as string, { 'active-route': attivaPerHub }]"
      tabindex="0"
      @click="itemClick($event, item)"
      @mouseenter="onMouseEnter"
    >
      <MenuIcon :icon="item['icon'] as string" />
      <span class="layout-menuitem-text">{{ item['label'] }}</span>
      <Tag
        v-if="item['nuova']"
        :value="t('layout.menu.nuovo', 'Nuovo')"
        severity="success"
        class="menu-badge-nuovo"
      />
    </router-link>

    <!-- Figli:
         - section (root, no icona): layout-root-submenulist, sempre nel DOM, nessun max-height
         - gruppi non-root: ul annidato, CSS gestisce max-height 0 → 1000px via active-menuitem -->
    <ul
      v-if="item['items'] && item['visible'] !== false"
      :class="{ 'layout-root-submenulist': isSection }"
    >
      <AppMenuItem
        v-for="child in (item['items'] as Record<string, unknown>[])"
        :key="child['id'] != null
          ? String(child['id'])
          : ((child['label'] as string) + '_' + (child['to'] || child['url'] || child['path'] || ''))"
        :item="child"
        :root="false"
        :parent-path="fullPath"
      />
    </ul>
  </li>
</template>

<style scoped>
/* Badge "Nuovo": voce passata di recente al portale nuovo (funzioni_2.migrata_il) */
.menu-badge-nuovo {
  margin-left: 0.4rem;
  font-size: 0.6rem;
  padding: 0 0.35rem;
  line-height: 1.2;
}
</style>
